import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useZoomPan, PointInspector } from './VerticalProfile';


function HorizontalTrack({ points, runway, analysis, clSynth, scoringContext }) {
  const canvasRef = useRef(null);
  const [selectedPt, setSelectedPt] = useState(null);
  const [showSynth, setShowSynth] = useState(true);
  const dataRef = useRef([]);
  const pointerDownRef = useRef(null);

  if (!points || points.length < 3 || !runway) return null;

  const rwLat = parseFloat(runway.threshold_lat);
  const rwLon = parseFloat(runway.threshold_lon);
  const apprType = scoringContext?.approachType || {};
  const apprLabel = apprType.label || '';

  // Prefer engine-generated clSynth (perfectly filtered centerline points).
  // Fall back to server analysis for older rows that predate clSynth storage.
  const src = (clSynth && clSynth.length > 3) ? clSynth : analysis;
  if (!src || src.length < 3) return null;
  const data = src
    .filter(a => a.distNm >= -0.1 && a.distNm <= 5 && Math.abs(a.crossTrackFt) < 2000)
    .sort((a, b) => a.distNm - b.distNm)
    .map(a => ({ dist: a.distNm, crosstrack: a.crossTrackFt, alt: a.agl || 0, synthetic: a.clSynthetic, _analysis: null }));
  if (data.length < 3) return null;
  dataRef.current=data;

  const maxDist=Math.min(1.1,Math.ceil(Math.max(...data.map(d=>d.dist))*10)/10);

  useEffect(()=>{
    const cv=canvasRef.current; if(!cv) return;
    const dpr=window.devicePixelRatio||1;
    const W=cv.parentElement.clientWidth||320, H=180;
    cv.width=Math.round(W*dpr); cv.height=Math.round(H*dpr);
    cv.style.width=W+'px'; cv.style.height=H+'px';
    const ctx=cv.getContext('2d'); ctx.scale(dpr,dpr);

    const ml=50,mr=12,mt=18,mb=28;
    const pw=W-ml-mr, ph=H-mt-mb;
    const xMin=-maxDist, xMax=0, xRng=xMax-xMin;
    const maxAbsCT=Math.max(150,...data.map(d=>Math.abs(d.crosstrack)));
    const yMaxFt=Math.ceil(Math.min(maxAbsCT*1.3,600)/50)*50;
    const xPx=nm=>ml+pw*((nm-xMin)/xRng);
    const yPx=ft=>mt+ph*(0.5-ft/(yMaxFt*2));
    const yc=ft=>Math.max(mt,Math.min(mt+ph,yPx(ft)));

    ctx.fillStyle='#0d0f14'; ctx.fillRect(0,0,W,H);

    function hband(xa,xb,ft,rgb,alpha){
      const yt=yc(ft),yb=yc(-ft);
      ctx.fillStyle=`rgba(${rgb},${alpha})`;
      ctx.fillRect(xPx(xa),Math.min(yt,yb),Math.abs(xPx(xb)-xPx(xa)),Math.abs(yb-yt));
    }
    function hline(xa,xb,ft,rgb,alpha){
      ctx.strokeStyle=`rgba(${rgb},${alpha})`; ctx.lineWidth=0.75; ctx.setLineDash([3,3]);
      ctx.beginPath(); ctx.moveTo(xPx(xa),yc(ft)); ctx.lineTo(xPx(xb),yc(ft)); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(xPx(xa),yc(-ft)); ctx.lineTo(xPx(xb),yc(-ft)); ctx.stroke();
      ctx.setLineDash([]);
    }
    const fapX=-Math.min(1.0,maxDist), vdesX=-Math.min(0.5,maxDist);
    hband(xMin,vdesX,50,'34,197,94',0.12); hband(xMin,vdesX,75,'34,197,94',0.06);
    hline(xMin,vdesX,50,'34,197,94',0.45); hline(xMin,vdesX,75,'234,179,8',0.40);
    hband(vdesX,0,25,'34,197,94',0.18); hband(vdesX,0,45,'34,197,94',0.08);
    hline(vdesX,0,25,'34,197,94',0.55); hline(vdesX,0,45,'234,179,8',0.50);

    const yStep=yMaxFt<=200?50:100;
    for(let ft=-yMaxFt;ft<=yMaxFt;ft+=yStep){
      ctx.strokeStyle=ft===0?'rgba(234,179,8,.4)':'#1e2230'; ctx.lineWidth=ft===0?1.5:0.75;
      ctx.setLineDash([]); ctx.beginPath(); ctx.moveTo(ml,yc(ft)); ctx.lineTo(ml+pw,yc(ft)); ctx.stroke();
      if(ft===0||Math.abs(ft)%yStep===0){
        ctx.fillStyle=ft===0?'rgba(234,179,8,.65)':'#4a5568'; ctx.font='9px monospace'; ctx.textAlign='right';
        ctx.fillText(ft===0?'CL':ft>0?'R '+ft:'L '+Math.abs(ft),ml-4,yc(ft)+3);
      }
    }

    [{nm:-Math.min(1.0,maxDist),label:'FAP',col:'rgba(154,205,100,.7)'},{nm:-Math.min(0.5,maxDist),label:'VDES',col:'rgba(234,179,8,.7)'}].forEach(({nm,label,col})=>{
      if(-nm>maxDist) return;
      const x=xPx(nm);
      ctx.save(); ctx.strokeStyle=col; ctx.lineWidth=1.5; ctx.setLineDash([5,3]);
      ctx.beginPath(); ctx.moveTo(x,mt); ctx.lineTo(x,mt+ph); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle=col; ctx.font='bold 9px monospace'; ctx.textAlign='center'; ctx.textBaseline='top';
      ctx.fillText(label,x,mt+2); ctx.textBaseline='alphabetic'; ctx.restore();
    });

    const xTicks=[-1.0,-0.75,-0.5,-0.25,0].filter(nm=>-nm<=maxDist);
    xTicks.forEach(nm=>{
      ctx.fillStyle='#4a5568'; ctx.font='9px monospace'; ctx.textAlign='center';
      ctx.fillText(nm===0?'THR':Math.abs(nm)+'nm',xPx(nm),mt+ph+16);
    });

    const drawData = showSynth ? data : data.filter(d=>!d.synthetic);
    ctx.save(); ctx.strokeStyle='rgba(59,130,246,.5)'; ctx.lineWidth=1.5;
    ctx.beginPath();
    drawData.forEach((d,i)=>{ const x=xPx(-d.dist),y=yc(d.crosstrack); i===0?ctx.moveTo(x,y):ctx.lineTo(x,y); });
    ctx.stroke(); ctx.restore();

    drawData.forEach(d=>{
      const ax=Math.abs(d.crosstrack);
      const col=d.dist<=0.5?(ax<=25?'#22c55e':ax<=45?'#eab308':'#ef4444'):(ax<=50?'#22c55e':ax<=75?'#eab308':'#ef4444');
      const r=d._analysis?2.5:2;
      ctx.beginPath(); ctx.arc(xPx(-d.dist),yc(d.crosstrack),r,0,Math.PI*2);
      if(d.synthetic){
        ctx.save(); ctx.globalAlpha=0.38; ctx.strokeStyle=col; ctx.lineWidth=1; ctx.stroke(); ctx.restore();
      } else {
        ctx.fillStyle=col; ctx.fill();
      }
    });

    if(selectedPt){
      ctx.beginPath(); ctx.arc(xPx(-selectedPt.distNm),yc(selectedPt.crossTrackFt),6,0,Math.PI*2);
      ctx.fillStyle='rgba(96,165,250,0.3)'; ctx.strokeStyle='#60a5fa'; ctx.lineWidth=2; ctx.fill(); ctx.stroke();
    }

    ctx.save(); ctx.beginPath(); ctx.arc(xPx(0),yc(0),5,0,Math.PI*2);
    ctx.fillStyle='#22c55e'; ctx.fill(); ctx.restore();

    ctx.fillStyle='rgba(255,255,255,.3)'; ctx.font='bold 10px monospace';
    ctx.textAlign='left'; ctx.textBaseline='top'; ctx.fillText('CENTERLINE TRACK · '+maxDist.toFixed(1)+'nm',ml+4,mt+3); ctx.textBaseline='alphabetic';
    ctx.fillStyle='rgba(255,255,255,.3)'; ctx.font='9px monospace'; ctx.textAlign='right'; ctx.textBaseline='top';
    ctx.fillText('RWY CL',ml+pw-2,mt+3); ctx.textBaseline='alphabetic';

  }, [data, selectedPt, maxDist, showSynth]);

  const handlePointerDown=e=>{pointerDownRef.current={x:e.clientX,y:e.clientY,t:Date.now()};};
  const handlePointerUp=e=>{
    const down=pointerDownRef.current; if(!down) return; pointerDownRef.current=null;
    const dt=Date.now()-down.t,dx=Math.abs(e.clientX-down.x),dy=Math.abs(e.clientY-down.y);
    if(dt<400&&dx<15&&dy<15){
      const cv=canvasRef.current; if(!cv) return;
      const rect=cv.getBoundingClientRect();
      const W=cv.parentElement.clientWidth||320,H=180;
      const ml=50,mr=12,mt=18,mb=28,pw=W-ml-mr,ph=H-mt-mb;
      const xMin=-maxDist,xMax=0,xRng=xMax-xMin;
      const maxAbsCT=Math.max(150,...dataRef.current.map(d=>Math.abs(d.crosstrack)));
      const yMaxFt=Math.ceil(Math.min(maxAbsCT*1.3,600)/50)*50;
      const xPx=nm=>ml+pw*((nm-xMin)/xRng);
      const yPx=ft=>mt+ph*(0.5-ft/(yMaxFt*2));
      const clickX=((e.clientX-rect.left)/rect.width)*W;
      const clickY=((e.clientY-rect.top)/rect.height)*H;
      let closest=null,closestD=Infinity;
      dataRef.current.filter(d=>showSynth || !d.synthetic).forEach(d=>{
        const dd=Math.sqrt((xPx(-d.dist)-clickX)**2+(yPx(d.crosstrack)-clickY)**2);
        if(dd<closestD){closestD=dd;closest=d;}
      });
      if(closest&&closestD<35){
        if(closest._analysis) setSelectedPt(closest._analysis);
        else setSelectedPt({distNm:closest.dist,agl:closest.alt||0,speed:0,crossTrackFt:closest.crosstrack,gsDevFt:0,flags:[],_synthetic:closest.synthetic});
      }
    }
  };

  return (
    <div style={{ marginTop:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <div className="section-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
          Horizontal Track
          {apprLabel && <span style={{ fontSize:12, padding:'2px 7px', borderRadius:10, background:apprType.model==='IFR'?'rgba(99,102,241,0.15)':'rgba(16,185,129,0.15)', color:apprType.model==='IFR'?'#818cf8':'#10b981', fontWeight:600 }}>{apprLabel}</span>}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={()=>setShowSynth(s=>!s)} style={{ fontSize:13, padding:'4px 10px', borderRadius:16, cursor:'pointer', border:`1px solid ${showSynth?'rgba(234,179,8,0.5)':'rgba(100,116,139,0.3)'}`, background:showSynth?'rgba(234,179,8,0.12)':'rgba(100,116,139,0.08)', color:showSynth?'#fbbf24':'#64748b', fontWeight:600 }}>{showSynth?'All pts':'Real only'}</button>
          {selectedPt && <button onClick={()=>setSelectedPt(null)} style={{ fontSize:13, padding:'4px 10px', borderRadius:16, cursor:'pointer', border:'1px solid #3b82f6', background:'rgba(59,130,246,0.15)', color:'#60a5fa', fontWeight:600 }}>× Close</button>}
        </div>
      </div>
      <div style={{ borderRadius:8, overflow:'hidden', border:`1px solid ${selectedPt?'rgba(59,130,246,0.4)':'var(--border)'}` }}>
        <canvas ref={canvasRef} style={{ display:'block', width:'100%', cursor:'crosshair', touchAction:'none' }}
          onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} />
      </div>
      <div style={{ display:'flex', gap:12, padding:'5px 2px 0', flexWrap:'wrap' }}>
        {[['#22c55e','good'],['#eab308','drift'],['#ef4444','chase'],['rgba(148,163,184,0.5)','synth']].map(([c,l])=>(
          <div key={l} style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, fontFamily:'monospace', color:'rgba(148,163,184,0.5)' }}>
            <div style={{ width:14, height:2, background:c, borderRadius:1 }}/>
            {l}
          </div>
        ))}
      </div>
      {selectedPt && <PointInspector point={selectedPt} scoringCtx={scoringContext} chartType="horizontal" onClose={()=>setSelectedPt(null)} />}
    </div>
  );
}

export { HorizontalTrack };
export default HorizontalTrack;
